import * as i0 from '@angular/core';
import { Directive, TemplateRef, Component, Input, ContentChild, EventEmitter, Output, HostBinding, ContentChildren, NgModule } from '@angular/core';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import { Subject, merge } from 'rxjs';
import { map } from 'rxjs/operators';
import { trigger, transition, style, animate, AUTO_STYLE } from '@angular/animations';

class AccordionContentDirective {
    constructor(template) {
        this.template = template;
    }
}
AccordionContentDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AccordionContentDirective, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive });
AccordionContentDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.12", type: AccordionContentDirective, selector: "[psUiAccordionContent]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AccordionContentDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[psUiAccordionContent]' }]
        }], ctorParameters: function () { return [{ type: i0.TemplateRef }]; } });

class AccordionTitleDirective {
    constructor(template) {
        this.template = template;
    }
}
AccordionTitleDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AccordionTitleDirective, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive });
AccordionTitleDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.12", type: AccordionTitleDirective, selector: "[psUiAccordionTitle]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AccordionTitleDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[psUiAccordionTitle]' }]
        }], ctorParameters: function () { return [{ type: i0.TemplateRef }]; } });

let uniqueId = 0;
class AccordionSectionComponent {
    constructor() {
        /** If true the accordion section cannot be expanded */
        this.disabled = false;
        /** An optional id for the section that must be unique to the page */
        this.id = `ps-ui-accordion-item-${uniqueId++}`;
        this._isExpanded = false;
        this._segmentToggled = new Subject();
        this.segmentToggled = this._segmentToggled.asObservable();
        /** Lazy loaded template that's wrapped in ng-template tags and using the psUiAccordionContent directive */
        this.templateContent = null;
        this.titleContent = null;
        this.arrowToggleOnly = false;
    }
    /** If true the section will start out expanded */
    set isExpanded(isExpanded) {
        this._isExpanded = isExpanded;
        this._segmentToggled.next(this._isExpanded);
    }
    get isExpanded() {
        return this._isExpanded;
    }
    toggle(event, isChevron = false) {
        event.stopPropagation();
        if (!this.arrowToggleOnly) {
            this.isExpanded = !this.isExpanded;
            return;
        }
        if (this.arrowToggleOnly && isChevron) {
            this.isExpanded = !this.isExpanded;
        }
    }
    open() {
        this.isExpanded = true;
    }
    close() {
        this.isExpanded = false;
    }
    ngOnDestroy() {
        // Ensure observables complete so parent component can unsubscribe
        this._segmentToggled.next();
        this._segmentToggled.complete();
    }
}
AccordionSectionComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AccordionSectionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
AccordionSectionComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.12", type: AccordionSectionComponent, selector: "ps-ui-accordion-section", inputs: { title: "title", disabled: "disabled", id: "id", tooltipText: "tooltipText", isExpanded: "isExpanded" }, queries: [{ propertyName: "templateContent", first: true, predicate: AccordionContentDirective, descendants: true, read: TemplateRef, static: true }, { propertyName: "titleContent", first: true, predicate: AccordionTitleDirective, descendants: true, read: TemplateRef, static: true }], ngImport: i0, template: "<div class=\"accordion-item\">\n  <h2 class=\"accordion-header\" [id]=\"id\">\n    <button\n      class=\"accordion-button\"\n      [class.collapsed]=\"!isExpanded\"\n      [class.arrow-toggle-only]=\"arrowToggleOnly\"\n      type=\"button\"\n      [attr.aria-expanded]=\"isExpanded\"\n      [attr.aria-controls]=\"id + '-panel'\"\n      (click)=\"toggle($event)\"\n      [disabled]=\"disabled\"\n    >\n      {{ title }}<ng-template *ngTemplateOutlet=\"titleContent\"></ng-template>\n      <button class=\"accordion-chevron\" (click)=\"toggle($event, true)\"></button>\n    </button>\n  </h2>\n  <div\n    [id]=\"id + '-section'\"\n    class=\"accordion-collapse collapse\"\n    [class.show]=\"isExpanded\"\n    [attr.aria-labelledby]=\"id\"\n    *ngIf=\"isExpanded\"\n    @collapse\n  >\n    <div class=\"accordion-body\">\n      <ng-template *ngTemplateOutlet=\"templateContent\"></ng-template>\n      <ng-content></ng-content>\n    </div>\n  </div>\n</div>\n", styles: [".accordion-button:disabled{color:#6c757d;pointer-events:none}.accordion-button:after,.accordion-button:disabled .accordion-chevron{display:none}.accordion-body{background-color:#fff}.accordion-chevron{flex-shrink:0;width:1.25rem;height:1.25rem;margin-left:auto;content:\"\";background-image:url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23212529'%3e%3cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3e%3c/svg%3e\");background-repeat:no-repeat;background-size:1.25rem;transition:transform .2s ease-in-out;border:none;background-color:transparent;padding:0}.accordion-button:not(.collapsed) .accordion-chevron{background-image:url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%230c63e4'%3e%3cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3e%3c/svg%3e\");transform:rotate(-180deg)}.arrow-toggle-only{cursor:default}.accordion-collapse{overflow:hidden}\n"], directives: [{ type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet"] }, { type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], animations: [
        trigger('collapse', [
            transition(':enter', [
                style({ height: '0', 'overflow-y': 'hidden' }),
                animate('0.3s ease-in-out', style({ height: AUTO_STYLE, 'overflow-y': AUTO_STYLE })),
            ]),
            transition(':leave', [
                animate('0.3s ease-in-out', style({ height: '0', 'overflow-y': 'hidden' })),
            ]),
        ]),
    ] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AccordionSectionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ps-ui-accordion-section', animations: [
                        trigger('collapse', [
                            transition(':enter', [
                                style({ height: '0', 'overflow-y': 'hidden' }),
                                animate('0.3s ease-in-out', style({ height: AUTO_STYLE, 'overflow-y': AUTO_STYLE })),
                            ]),
                            transition(':leave', [
                                animate('0.3s ease-in-out', style({ height: '0', 'overflow-y': 'hidden' })),
                            ]),
                        ]),
                    ], template: "<div class=\"accordion-item\">\n  <h2 class=\"accordion-header\" [id]=\"id\">\n    <button\n      class=\"accordion-button\"\n      [class.collapsed]=\"!isExpanded\"\n      [class.arrow-toggle-only]=\"arrowToggleOnly\"\n      type=\"button\"\n      [attr.aria-expanded]=\"isExpanded\"\n      [attr.aria-controls]=\"id + '-panel'\"\n      (click)=\"toggle($event)\"\n      [disabled]=\"disabled\"\n    >\n      {{ title }}<ng-template *ngTemplateOutlet=\"titleContent\"></ng-template>\n      <button class=\"accordion-chevron\" (click)=\"toggle($event, true)\"></button>\n    </button>\n  </h2>\n  <div\n    [id]=\"id + '-section'\"\n    class=\"accordion-collapse collapse\"\n    [class.show]=\"isExpanded\"\n    [attr.aria-labelledby]=\"id\"\n    *ngIf=\"isExpanded\"\n    @collapse\n  >\n    <div class=\"accordion-body\">\n      <ng-template *ngTemplateOutlet=\"templateContent\"></ng-template>\n      <ng-content></ng-content>\n    </div>\n  </div>\n</div>\n", styles: [".accordion-button:disabled{color:#6c757d;pointer-events:none}.accordion-button:after,.accordion-button:disabled .accordion-chevron{display:none}.accordion-body{background-color:#fff}.accordion-chevron{flex-shrink:0;width:1.25rem;height:1.25rem;margin-left:auto;content:\"\";background-image:url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23212529'%3e%3cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3e%3c/svg%3e\");background-repeat:no-repeat;background-size:1.25rem;transition:transform .2s ease-in-out;border:none;background-color:transparent;padding:0}.accordion-button:not(.collapsed) .accordion-chevron{background-image:url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%230c63e4'%3e%3cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3e%3c/svg%3e\");transform:rotate(-180deg)}.arrow-toggle-only{cursor:default}.accordion-collapse{overflow:hidden}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { title: [{
                type: Input
            }], disabled: [{
                type: Input
            }], id: [{
                type: Input
            }], tooltipText: [{
                type: Input
            }], isExpanded: [{
                type: Input
            }], templateContent: [{
                type: ContentChild,
                args: [AccordionContentDirective, { read: TemplateRef, static: true }]
            }], titleContent: [{
                type: ContentChild,
                args: [AccordionTitleDirective, { read: TemplateRef, static: true }]
            }] } });

class AccordionDirective {
    constructor() {
        /** If true only one section can be open at once */
        this.closeOthers = false;
        this.toggle = new EventEmitter();
        this._arrowToggleOnly = false;
        this.defaultClass = 'accordion';
    }
    /** If true the section can only be opened by clicking the arrow on the side */
    set arrowToggleOnly(toggle) {
        this._arrowToggleOnly = toggle;
        if (this.sections) {
            this._updateArrowToggleOnlyOnSections();
        }
    }
    ngAfterContentInit() {
        this._updateArrowToggleOnlyOnSections();
    }
    ngAfterViewInit() {
        // Subscribe to all sections toggled status so we can
        // close the others if closeOthers is set to true
        const obs = this.sections.map((section, index) => {
            return section.segmentToggled.pipe(map((opened) => ({ index, opened })));
        });
        merge(...obs).subscribe((update) => {
            if (update.opened && this.closeOthers) {
                this.sections.forEach((section, i) => {
                    if (update.index !== i) {
                        section.close();
                    }
                });
            }
            this.toggle.emit(update);
        });
    }
    _updateArrowToggleOnlyOnSections() {
        this.sections.forEach((section) => (section.arrowToggleOnly = this._arrowToggleOnly));
    }
}
AccordionDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AccordionDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
AccordionDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.12", type: AccordionDirective, selector: "[psUiAccordion], ps-ui-accordion", inputs: { closeOthers: "closeOthers", arrowToggleOnly: "arrowToggleOnly" }, outputs: { toggle: "toggle" }, host: { properties: { "class": "this.defaultClass" } }, queries: [{ propertyName: "sections", predicate: AccordionSectionComponent }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AccordionDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[psUiAccordion], ps-ui-accordion' }]
        }], propDecorators: { closeOthers: [{
                type: Input
            }], toggle: [{
                type: Output
            }], arrowToggleOnly: [{
                type: Input
            }], defaultClass: [{
                type: HostBinding,
                args: ['class']
            }], sections: [{
                type: ContentChildren,
                args: [AccordionSectionComponent]
            }] } });

const DECLARATIONS = [
    AccordionDirective,
    AccordionSectionComponent,
    AccordionContentDirective,
    AccordionTitleDirective,
];
class AccordionModule {
}
AccordionModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AccordionModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AccordionModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AccordionModule, declarations: [AccordionDirective,
        AccordionSectionComponent,
        AccordionContentDirective,
        AccordionTitleDirective], imports: [CommonModule], exports: [AccordionDirective,
        AccordionSectionComponent,
        AccordionContentDirective,
        AccordionTitleDirective] });
AccordionModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AccordionModule, imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: AccordionModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DECLARATIONS,
                    exports: DECLARATIONS,
                    imports: [CommonModule],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AccordionContentDirective, AccordionDirective, AccordionModule, AccordionSectionComponent, AccordionTitleDirective };
